/*
 * Alert: an inline message inside a page or form. Banner: the full-width strip in the shell's banner slot.
 *   <Alert tone="warning" title={…}>{body}</Alert>
 *   <Banner tone="danger" icon={<CreditCard />} action={<Button size="sm">…</Button>} onDismiss={…}>{text}</Banner>
 */
import { CheckCircle, Info, Warning, WarningOctagon, X } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

export type AlertTone = 'info' | 'success' | 'warning' | 'danger'

const TONES: Record<AlertTone, { box: string; icon: string; Icon: typeof Info }> = {
  info: { box: 'bg-brand-50 ring-brand-100', icon: 'text-brand-600', Icon: Info },
  success: { box: 'bg-teal-50 ring-teal-100', icon: 'text-teal-600', Icon: CheckCircle },
  warning: { box: 'bg-warm-50 ring-warm-200', icon: 'text-warm-700', Icon: Warning },
  danger: { box: 'bg-danger-50 ring-danger-100', icon: 'text-danger-600', Icon: WarningOctagon },
}

export function Alert({
  tone = 'info',
  title,
  children,
  action,
  icon,
  onDismiss,
  className,
}: {
  tone?: AlertTone
  title?: ReactNode
  children?: ReactNode
  action?: ReactNode
  icon?: ReactNode
  onDismiss?: () => void
  className?: string
}) {
  const { t } = useTranslation('common')
  const s = TONES[tone]
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-2xl p-4 ring-1 ring-inset', s.box, className)}
    >
      <span className={cn('mt-0.5 shrink-0 [&_svg]:size-5', s.icon)} aria-hidden="true">
        {icon ?? <s.Icon weight="fill" />}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title ? <p className="text-14 font-semibold text-ink">{title}</p> : null}
        {children ? <div className="text-14 leading-6 text-body">{children}</div> : null}
        {action ? <div className="mt-2 flex flex-wrap gap-2">{action}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('actions.dismiss')}
          className="-m-1 grid size-8 shrink-0 place-items-center rounded-lg text-muted transition-colors hover:bg-control hover:text-ink"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )
}

const BANNER: Record<AlertTone, string> = {
  info: 'bg-brand-50 ring-brand-100 [&_[data-icon]]:text-brand-600',
  success: 'bg-teal-50 ring-teal-100 [&_[data-icon]]:text-teal-600',
  warning: 'bg-warm-50 ring-warm-200 [&_[data-icon]]:text-warm-700',
  danger: 'bg-danger-50 ring-danger-100 [&_[data-icon]]:text-danger-600',
}

export function Banner({
  tone = 'info',
  icon,
  children,
  action,
  onDismiss,
  className,
}: {
  tone?: AlertTone
  icon?: ReactNode
  children: ReactNode
  action?: ReactNode
  onDismiss?: () => void
  className?: string
}) {
  const { t } = useTranslation('common')
  const Icon = TONES[tone].Icon
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded-2xl px-4 py-2.5 ring-1 ring-inset sm:items-center',
        BANNER[tone],
        className,
      )}
    >
      <span data-icon="" className="mt-0.5 shrink-0 sm:mt-0 [&_svg]:size-5" aria-hidden="true">
        {icon ?? <Icon weight="fill" />}
      </span>
      {/* phones: text, then the action under it; from sm: one line */}
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="min-w-0 flex-1 text-14 leading-6 text-ink">{children}</div>
        {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('actions.dismiss')}
          className="grid size-8 shrink-0 place-items-center rounded-lg text-muted transition-colors hover:bg-control hover:text-ink touch:size-11"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )
}
